import { formatInTimeZone } from 'date-fns-tz';
import { ChevronLeft, ChevronRight, Loader2, Plus } from 'lucide-react';
import { Fragment, useCallback, useMemo, useState } from 'react';

import {
    AvailabilityFormDialog,
    type EditTarget,
} from '@apps/web/components/caregivers/availability_form_dialog';
import {
    AvailabilityDetailsDialog,
    ConflictBadge,
    WeeklyEntryDetails,
} from '@apps/web/components/caregivers/caregiver_availability_details';
import { AvailabilityInstructionComposer } from '@apps/web/components/caregivers/caregiver_availability_instruction_composer';
import {
    AvailabilityLegend,
    AvailabilityMonthGrid,
} from '@apps/web/components/caregivers/caregiver_availability_month_grid';
import {
    buildEffectiveAvailabilityItems,
    buildTimeboxedDayGroups,
    buildWeeklyGridSegments,
    findAvailabilitySegmentById,
    mergeWeeklyAvailability,
    minuteOfWeekToDayKey,
    minuteOfWeekToTime,
    type TimeboxedAvailabilityEntry,
    type TimelineItem,
    type WeeklyAvailabilityEntry,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { CaregiverShiftDetailsDialog } from '@apps/web/components/caregivers/caregiver_shift_details_dialog';
import { WeeklyPatternGrid } from '@apps/web/components/caregivers/caregiver_weekly_pattern_grid';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { SegmentedControl } from '@apps/web/components/ui/segmented_control';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    addMonthsToDateKey,
    endOfMonthDateKey,
    formatMonthKey,
    mondayStartDateKey,
    startOfMonthDateKey,
    toDateKey,
} from '@apps/web/lib/date_utils';
import { useCaregiverAvailabilityProfile } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type CaregiverAvailabilityProfileProps = {
    readonly organizationId: string | null;
    readonly caregiverId: string | null;
    readonly externalSourceLabel?: string;
};

type AvailabilityViewMode = 'calendar' | 'weekly';

export function CaregiverAvailabilityProfile({
    organizationId,
    caregiverId,
    externalSourceLabel,
}: CaregiverAvailabilityProfileProps) {
    const { timezone, isLoading: isTimezoneLoading } =
        useOrganizationTimezone();
    const [viewMode, setViewMode] = useState<AvailabilityViewMode>('calendar');
    // Null until the user navigates; the visible period then follows today.
    const [anchorOverrideKey, setAnchorOverrideKey] = useState<string | null>(
        null,
    );
    const [detailSegmentId, setDetailSegmentId] = useState<string | null>(null);
    const [detailShiftId, setDetailShiftId] = useState<string | null>(null);
    const [detailWeeklySegmentId, setDetailWeeklySegmentId] = useState<
        string | null
    >(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<EditTarget | undefined>(
        undefined,
    );
    const [deleteTarget, setDeleteTarget] = useState<{
        readonly type: 'one-time' | 'weekly';
        readonly id: string;
    } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const isTimezoneReady = !isTimezoneLoading && !!timezone;

    const todayKey = useMemo(
        () => (isTimezoneReady ? toDateKey(new Date(), timezone) : null),
        [isTimezoneReady, timezone],
    );

    const anchorDateKey = anchorOverrideKey ?? todayKey;

    const rangeStartDateKey = anchorDateKey
        ? startOfMonthDateKey(anchorDateKey)
        : null;
    const rangeEndDateKey = rangeStartDateKey
        ? endOfMonthDateKey(rangeStartDateKey)
        : null;

    // Weekly-pattern time labels need a concrete reference week; use the
    // week containing the visible month's start.
    const referenceWeekStartKey = useMemo(
        () =>
            rangeStartDateKey ? mondayStartDateKey(rangeStartDateKey) : null,
        [rangeStartDateKey],
    );

    const {
        data,
        isLoading: isProfileLoading,
        error,
        mutate,
    } = useCaregiverAvailabilityProfile(
        organizationId,
        caregiverId,
        rangeStartDateKey,
        rangeEndDateKey,
    );

    const isLoading = isTimezoneLoading || isProfileLoading;

    const calendarItems = useMemo(() => {
        if (!data || !rangeStartDateKey || !rangeEndDateKey) {
            return [];
        }

        const items: Array<TimelineItem> = [
            ...buildEffectiveAvailabilityItems(
                data.availability,
                data.weekly_availability,
                externalSourceLabel,
                rangeStartDateKey,
                rangeEndDateKey,
                timezone,
            ),
        ];

        data.shifts.forEach((shift) => {
            const detailParts = [shift.patient_name];
            if (shift.role_required) {
                detailParts.push(shift.role_required.toUpperCase());
            }
            items.push({
                kind: 'shift',
                id: `shift-${shift.id}`,
                type: 'shift',
                startTime: shift.shift_start_time,
                endTime: shift.shift_end_time,
                details: detailParts.join(' | '),
            });
        });

        return items;
    }, [
        data,
        rangeStartDateKey,
        rangeEndDateKey,
        externalSourceLabel,
        timezone,
    ]);

    const weeklyItems = useMemo(() => {
        if (!data) {
            return [];
        }

        return mergeWeeklyAvailability(
            data.weekly_availability,
            externalSourceLabel,
        );
    }, [data, externalSourceLabel]);

    const calendarDayGroups = useMemo(
        () => buildTimeboxedDayGroups(calendarItems, timezone),
        [calendarItems, timezone],
    );

    const detailSegment = useMemo(
        () => findAvailabilitySegmentById(calendarDayGroups, detailSegmentId),
        [calendarDayGroups, detailSegmentId],
    );

    const detailShift = useMemo(
        () => data?.shifts.find((shift) => shift.id === detailShiftId) ?? null,
        [data, detailShiftId],
    );

    const weeklyGridSegments = useMemo(() => {
        if (!referenceWeekStartKey) {
            return [];
        }
        return buildWeeklyGridSegments(
            weeklyItems,
            referenceWeekStartKey,
            timezone,
        );
    }, [timezone, referenceWeekStartKey, weeklyItems]);

    const detailWeeklySegment = useMemo(
        () =>
            weeklyGridSegments.find(
                (segment) => segment.id === detailWeeklySegmentId,
            ) ?? null,
        [weeklyGridSegments, detailWeeklySegmentId],
    );

    const rangeLabel = rangeStartDateKey
        ? formatMonthKey(rangeStartDateKey, timezone)
        : 'Month';

    const handleNavigate = useCallback(
        (direction: -1 | 1) => {
            setAnchorOverrideKey((current) => {
                const startKey = current ?? rangeStartDateKey;
                if (!startKey) {
                    return current;
                }
                return addMonthsToDateKey(
                    startOfMonthDateKey(startKey),
                    direction,
                );
            });
        },
        [rangeStartDateKey],
    );

    const handleAdd = useCallback(() => {
        setEditTarget(undefined);
        setDialogOpen(true);
    }, []);

    const handleEditOneTime = useCallback(
        (entry: TimeboxedAvailabilityEntry) => {
            setEditTarget({
                type: 'one-time',
                id: entry.id,
                status: entry.status,
                startDate: formatInTimeZone(
                    entry.startTime,
                    timezone,
                    'yyyy-MM-dd',
                ),
                startTime: formatInTimeZone(entry.startTime, timezone, 'HH:mm'),
                endDate: formatInTimeZone(
                    entry.endTime,
                    timezone,
                    'yyyy-MM-dd',
                ),
                endTime: formatInTimeZone(entry.endTime, timezone, 'HH:mm'),
            });
            setDialogOpen(true);
        },
        [timezone],
    );

    const handleEditWeekly = useCallback((entry: WeeklyAvailabilityEntry) => {
        const endTime = minuteOfWeekToTime(entry.endMinuteOfWeek, {
            isEndTime: true,
        });
        setEditTarget({
            type: 'weekly',
            id: entry.id,
            status: entry.status,
            dayOfWeek: minuteOfWeekToDayKey(entry.startMinuteOfWeek),
            startTime: minuteOfWeekToTime(entry.startMinuteOfWeek),
            endTime,
        });
        setDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!organizationId || !caregiverId || !deleteTarget) {
            return;
        }
        setIsDeleting(true);
        try {
            if (deleteTarget.type === 'one-time') {
                await getApiClient().delete({
                    url: '/organizations/{organization_id}/caregivers/{caregiver_id}/availability/{availability_id}',
                    pathParams: {
                        organization_id: organizationId,
                        caregiver_id: caregiverId,
                        availability_id: deleteTarget.id,
                    },
                });
                toast.success('Availability deleted', {
                    persistNotification: false,
                });
            } else {
                await getApiClient().delete({
                    url: '/organizations/{organization_id}/caregivers/{caregiver_id}/weekly-availability/{weekly_availability_id}',
                    pathParams: {
                        organization_id: organizationId,
                        caregiver_id: caregiverId,
                        weekly_availability_id: deleteTarget.id,
                    },
                });
                toast.success('Weekly availability deleted', {
                    persistNotification: false,
                });
            }
            mutate();
        } catch (error) {
            toast.error(
                error instanceof Error && error.message
                    ? error.message
                    : 'Failed to delete availability',
                { persistNotification: false },
            );
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    }, [organizationId, caregiverId, deleteTarget, mutate]);

    const hasIds = !!organizationId && !!caregiverId;

    return (
        <section className="space-y-3">
            <header className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="flex min-h-8 items-center text-base font-semibold">
                        Availability
                    </h2>
                    <SegmentedControl
                        options={[
                            { value: 'calendar', label: 'Calendar' },
                            { value: 'weekly', label: 'Weekly pattern' },
                        ]}
                        value={viewMode}
                        onValueChange={setViewMode}
                        size="sm"
                        aria-label="Availability view mode"
                    />
                </div>
                {viewMode === 'calendar' ? (
                    <div className="flex items-center gap-0.5">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            onClick={() => handleNavigate(-1)}
                            disabled={!rangeStartDateKey}
                            aria-label="Previous month"
                        >
                            <ChevronLeft className="size-3.5" />
                        </Button>
                        <span className="text-xs font-medium">
                            {rangeLabel}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            onClick={() => handleNavigate(1)}
                            disabled={!rangeStartDateKey}
                            aria-label="Next month"
                        >
                            <ChevronRight className="size-3.5" />
                        </Button>
                    </div>
                ) : null}
            </header>
            <div className="space-y-3">
                {!hasIds ? (
                    <div className="text-muted-foreground text-sm">
                        Select a caregiver to view availability.
                    </div>
                ) : isLoading ? (
                    <div className="flex h-32 items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                ) : error ? (
                    <div className="py-6">
                        <p className="text-muted-foreground text-sm">
                            Failed to load availability data.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            {viewMode === 'calendar' &&
                            rangeStartDateKey &&
                            todayKey ? (
                                <Fragment>
                                    <AvailabilityMonthGrid
                                        monthStartKey={rangeStartDateKey}
                                        todayKey={todayKey}
                                        timezone={timezone}
                                        dayGroups={calendarDayGroups}
                                        onAvailabilityClick={(segment) =>
                                            setDetailSegmentId(segment.id)
                                        }
                                        onShiftClick={(shiftItemId) =>
                                            setDetailShiftId(
                                                shiftItemId.replace(
                                                    /^shift-/u,
                                                    '',
                                                ),
                                            )
                                        }
                                        hideLegend={true}
                                    />
                                    {calendarDayGroups.length === 0 ? (
                                        <p className="text-muted-foreground mt-2 text-sm">
                                            No availability, shifts, or weekly
                                            patterns recorded for this month.
                                        </p>
                                    ) : null}
                                </Fragment>
                            ) : weeklyItems.length === 0 ? (
                                <div className="text-muted-foreground text-sm">
                                    No weekly availability patterns set.
                                </div>
                            ) : (
                                <WeeklyPatternGrid
                                    segments={weeklyGridSegments}
                                    onSegmentClick={(segment) =>
                                        setDetailWeeklySegmentId(segment.id)
                                    }
                                />
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {viewMode === 'calendar' ? (
                                <AvailabilityLegend className="shrink-0" />
                            ) : null}
                            <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={handleAdd}
                            >
                                <Plus className="mr-1 h-4 w-4" />
                                Add Availability
                            </Button>
                            <AvailabilityInstructionComposer
                                organizationId={organizationId}
                                caregiverId={caregiverId}
                                onParsed={mutate}
                            />
                        </div>
                    </div>
                )}
            </div>
            {hasIds && organizationId && caregiverId && (
                <AvailabilityFormDialog
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    organizationId={organizationId}
                    caregiverId={caregiverId}
                    timezone={timezone}
                    editTarget={editTarget}
                    onSuccess={() => mutate()}
                />
            )}
            <AvailabilityDetailsDialog
                segment={detailSegment}
                timezone={timezone}
                onClose={() => setDetailSegmentId(null)}
                onEditEntry={(entry) => {
                    setDetailSegmentId(null);
                    handleEditOneTime(entry);
                }}
                onDeleteEntry={(entry) => {
                    setDetailSegmentId(null);
                    setDeleteTarget({
                        type: 'one-time',
                        id: entry.id,
                    });
                }}
            />
            <Dialog
                open={detailWeeklySegment !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setDetailWeeklySegmentId(null);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            Weekly pattern details
                            {detailWeeklySegment?.hasConflict ? (
                                <ConflictBadge />
                            ) : null}
                        </DialogTitle>
                        <DialogDescription>
                            {detailWeeklySegment
                                ? `${detailWeeklySegment.dayLabel}s`
                                : null}
                        </DialogDescription>
                    </DialogHeader>
                    {detailWeeklySegment ? (
                        <WeeklyEntryDetails
                            entrySegments={detailWeeklySegment.entrySegments}
                            onEditEntry={(entry) => {
                                setDetailWeeklySegmentId(null);
                                handleEditWeekly(entry);
                            }}
                            onDeleteEntry={(entry) => {
                                setDetailWeeklySegmentId(null);
                                setDeleteTarget({
                                    type: 'weekly',
                                    id: entry.id,
                                });
                            }}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>
            <CaregiverShiftDetailsDialog
                shift={detailShift}
                timezone={timezone}
                onClose={() => setDetailShiftId(null)}
            />
            <Dialog
                open={deleteTarget !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setDeleteTarget(null);
                    }
                }}
            >
                <DialogContent showCloseButton={false}>
                    <DialogHeader>
                        <DialogTitle>Delete availability</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this{' '}
                            {deleteTarget?.type === 'weekly'
                                ? 'weekly availability'
                                : 'availability'}{' '}
                            entry? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteTarget(null)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => void handleConfirmDelete()}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}
